import {useMutation} from '@tanstack/react-query'
import {Sidebar} from 'primereact/sidebar'
import {forwardRef, useCallback, useImperativeHandle, useState} from 'react'
import {createOrUpdateGenericInfo} from '../../api/ServiceWorker'
import TranslateUtils from '../../i18n/TranslateUtils'
import {
    blockOverflownScroll,
    unblockOverflownScroll,
} from '../../utils/BlockScrollsUtils'
import {useAuth} from '../authentication/AuthProvider'
import CustomForm from '../form-utils/CustomForm'
import {formLayout} from '../form/Constant'
import FormHeader from '../form/FormHeader'
import {severityNotification} from '../notification/NotificationConstant'
import {showNotification} from '../notification/NotificationService'
import {
    deviceTypes,
    getObsColors,
    obsColWrapperData,
    observationKeys,
} from '../observations/Constants'
import {getIndexOfValueInMultiCol} from '../observations/Utils'
import {patientObservationListForm} from './PatientPageConstants'

const PATH = 'healthRecord'

const PatientObservationForm = forwardRef((props, ref) => {
    useImperativeHandle(ref, () => ({
        showForm: () => showForm(),
        hideForm: () => hideForm(),
    }))
    const {translateCommons} = TranslateUtils()
    const [visible, setVisible] = useState(false)
    const {user} = useAuth()
    const [news, setNews] = useState(0)
    const [newsLog, setNewsLog] = useState({})
    const showForm = () => {
        blockOverflownScroll()
        setVisible(true)
    }

    const hideForm = () => {
        unblockOverflownScroll()
        setVisible(false)
    }

    const {mutate} = useMutation(
        data => createOrUpdateGenericInfo(PATH, user?.id, data),
        {
            onSuccess: data => {
                if (data?.id) {
                    showNotification(
                        severityNotification.success,
                        'Observation Created Successfully !!',
                    )

                    props?.reload(data)

                    hideForm()

                    if (!props.patient) {
                        setNews(0)
                        setNewsLog({})
                    }
                }
            },
        },
    )

    const getColorByValue = (colorList, dataRange, value, isNews = false) => {
        if (
            !colorList ||
            !Array.isArray(colorList) ||
            !dataRange ||
            !Array.isArray(dataRange) ||
            !value
        ) {
            return ''
        }

        const obsColors = getObsColors()
        if (!isNews) {
            if (obsColors && obsColors.lv1) {
                colorList = colorList.map(x =>
                    x.replace(obsColors.lv1, '#44cc61'),
                )
            }

            const headColor = colorList[0]
            const tailColor = colorList[colorList.length - 1]

            colorList.push(tailColor)
            colorList.unshift(headColor)
        }

        let colorValue = ''
        let colorIndex = getIndexOfValueInMultiCol(dataRange, value)
        if (colorIndex !== null) {
            if (!isNews) {
                colorValue = colorList[colorIndex + 1]
            } else {
                colorValue = colorList[colorIndex]
            }
        }

        return colorValue
    }

    const getValueOfTheColor = color => {
        const obsColors = getObsColors()
        if (obsColors && Object.keys(obsColors).length > 0) {
            let index = null
            for (const key in obsColors) {
                if (obsColors[key] === color) {
                    index = key
                }
            }

            if (index) {
                switch (index) {
                    case 'lv1':
                        return 0
                    case 'lv2':
                        return 1
                    case 'lv3':
                        return 2
                    case 'lv4':
                        return 3
                    default:
                        return 0
                }
            }
        }
    }

    const onChangeNewsValue = useCallback(
        data => {
            let newsObj = {
                [observationKeys.levelOfConsciousness]:
                    data.levelOfConsciousness,
                [observationKeys.heartRate]: data.heartRate,
                [observationKeys.respiration]: data.respiration,
                [observationKeys.oxygen]: data.oxygen,
                [observationKeys.temperature]: data.temperature,
                [observationKeys.bloodPressure]: data.systolic,
            }
            let confusionValue = data.confusion
            let newsValue = 0
            let holderDialog = []
            let newsLogObj = {}
            for (const key in newsObj) {
                if (newsObj[key]) {
                    if (key === observationKeys.levelOfConsciousness) {
                        const allowedConsciousnessKeys = [
                            translateCommons('voice'),
                            translateCommons('confusion'),
                            translateCommons('unresponsive'),
                            translateCommons('pain'),
                        ]
                        if (allowedConsciousnessKeys.includes(newsObj[key])) {
                            if (key === 'confusion') {
                                if (confusionValue === true) {
                                    newsValue += 3
                                    newsLogObj = {
                                        ...newsLogObj,
                                        [key]: [newsObj[key]],
                                    }
                                } else {
                                    newsValue = 0
                                }
                            } else {
                                newsValue += 3
                                newsLogObj = {
                                    ...newsLogObj,
                                    [key]: [newsObj[key]],
                                }
                            }
                        } else {
                            newsValue = 0
                            delete newsLogObj[key]
                        }
                    } else {
                        for (let j = 0; j < obsColWrapperData.length; j++) {
                            if (
                                obsColWrapperData[j].key === key &&
                                obsColWrapperData[j].colors &&
                                obsColWrapperData[j].dataRange
                            ) {
                                const colorValue = getColorByValue(
                                    obsColWrapperData[j].colors,
                                    obsColWrapperData[j].dataRange,
                                    newsObj[key],
                                    true,
                                )

                                const valueOfTheColor =
                                    getValueOfTheColor(colorValue)
                                newsValue += valueOfTheColor
                                if (valueOfTheColor === 3) {
                                    holderDialog.push(key)
                                }
                                newsLogObj = {
                                    ...newsLogObj,
                                    [key]: valueOfTheColor,
                                }
                                break
                            }
                        }
                    }
                } else {
                    newsValue = 0
                    break
                }
            }

            if (newsValue > 0) {
                setNews(newsValue)
                setNewsLog(newsLogObj)
            } else {
                setNews(0)
                setNewsLog({})
            }
        },
        [translateCommons],
    )

    const handleOnSubmit = useCallback(
        async data => {
            let toPostData = {
                ...data,
                userId: user?.id,
                news: news,
                newsLog: newsLog ? newsLog : {},
                device: deviceTypes.computer.value,
            }

            mutate(toPostData)
        },
        [mutate, news, newsLog, user?.id],
    )
    return (
        <Sidebar
            className="p-sidebar-full"
            visible={visible}
            blockScroll
            onHide={() => hideForm()}
            fullScreen>
            <FormHeader
                header={translateCommons('OBSERVATION FORM')}
                onHide={() => hideForm()}
            />
            <CustomForm
                onSubmit={handleOnSubmit}
                layout={formLayout.group}
                dataList={patientObservationListForm}
                observation
                information
                patient
                userData={user}
                onChangeCustomValue={onChangeNewsValue}
            />
        </Sidebar>
    )
})

export default PatientObservationForm
